import { Injectable } from '@angular/core';
import { MaintenanceFilter } from '@core/interfaces';
import { TranslateService } from '@ngx-translate/core';
import { Apollo } from 'apollo-angular';
import gql from 'graphql-tag';
import { chain, sumBy } from 'lodash';

export interface ResultItem {
  key: string;
  value: string;
}

export interface MaintenancesByStatus {
  new: number;
  pending: number;
  scheduled: number;
  inProgress: number;
  done: number;
  reported: number;
  canceled: number;
  requested: number;
  onNegotiation: number;
}

function positionByPeriod(period: string): number {
  const map = {
    'up_to_2_days': 0,
    'between_3_and_6_days': 1,
    'between_7_and_15_days': 2,
    'more_than_15_days': 3,
  };

  return map[period];
}

@Injectable({
  providedIn: 'root'
})
export class DashboardMaintenancesService {

  constructor(
    private readonly apollo: Apollo,
    private readonly translate: TranslateService
  ) { }

  async accountMaintenancesTypes(where: MaintenanceFilter): Promise<any> {
    const query = gql`
      query accountMaintenancesTypes ($where: DashboardInput!) {
        accountMaintenancesTypes(where: $where, limit: 10) {
          reportName
          items {
            name: key
            value
            extra
          }
        }
      }
    `;

    return this.apollo
      .query<any>({
        query,
        variables: { where },
      })
      .toPromise()
      .then(({ data }) => {
        return chain(data.accountMaintenancesTypes.items)
          .orderBy('value')
          .reverse()
          .value();
      });
  }

  async accountMaintenancesSegments(where: MaintenanceFilter): Promise<any> {
    const query = gql`
      query accountMaintenancesSegments ($where: DashboardInput!) {
        accountMaintenancesSegments(where: $where, limit: 10) {
          reportName
          items {
            name: key
            value
            extra
          }
        }
      }
    `;

    return this.apollo
      .query<any>({
        query,
        variables: { where },
      })
      .toPromise()
      .then(({ data }) => {
        return chain(data.accountMaintenancesSegments.items)
          .orderBy('value')
          .reverse()
          .value();
      });
  }

  async accountMaintenancesTopPendingTime(where: MaintenanceFilter): Promise<any> {
    const query = gql`
      query accountMaintenancesTopPendingTime ($where: DashboardInput!) {
        accountMaintenancesTopPendingTime(where: $where, limit: 10) {
          reportName
          items {
            name: key
            value
            extra
          }
        }
      }
    `;

    return this.apollo
      .query<any>({
        query,
        variables: { where },
      })
      .toPromise()
      .then(({ data }) => {
        return chain(data.accountMaintenancesTopPendingTime.items)
          .orderBy('value')
          .reverse()
          .map((item) => {
            item.extra.location = { name: item.name };
            item.name = `${item.name} • ${item.extra.maintenance.number}`;
            return item;
          })
          .value();
      });
  }

  async accountMaintenancesPendingTimeInterval(where: MaintenanceFilter): Promise<any> {
    const query = gql`
      query accountMaintenancesPendingTimeInterval ($where: DashboardInput!) {
        accountMaintenancesPendingTimeInterval(where: $where) {
          reportName
          items {
            name: key
            value
            extra
          }
        }
      }
    `;

    return this.apollo
      .query<any>({
        query,
        variables: { where },
      })
      .toPromise()
      .then(({ data }) => {
        return chain(data.accountMaintenancesPendingTimeInterval.items)
          .orderBy((item) => positionByPeriod(item.name))
          .map((value) => {
            const key = `MAINTENANCES_REPORT.OPEN_MAINTENANCE_PER_PERIOD.${value.name}`.toUpperCase();
            value.name = this.translate.instant(key);
            return value;
          })
          .value();
      });
  }
}
